import { Component } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, IntlContext } from 'dibs-react-intl';

// Helpers
import { mobile as mobileCheck } from 'dibs-client-check';
import { getFolderLink, getInvoiceLink, types, typesRequiringName } from '../utils/constants';
import { checkboxLabel, HeaderTitle, SubmitLabel } from '../messages/shareModalMessages';

import { BarHeader } from 'dibs-elements/exports/BarHeader';
import { Checkbox } from 'dibs-elements/exports/Checkbox';
import { ModalBody } from 'dibs-elements/exports/ModalBody';
import { ModalCloseButton } from 'dibs-elements/exports/ModalCloseButton';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer';
import { Input } from 'dibs-elements/exports/Input';
import { TextArea } from 'dibs-elements/exports/TextArea';

import ValidatedInput, { validations } from 'dibs-validated-input';
import ItemPreviews from './ItemPreviews';
import CopyToClipboard from './CopyToClipboard';
import Submit from './Submit';

// Styles
import styles from '../styles/shareModal.scss';

// These are placeholders for Inputs, Textarea
const placeholders = defineMessages({
    emails: {
        id: 'shareModal.placeholder.emails',
        defaultMessage: 'Enter recipient emails, separated by commas',
    },
    emailsMobile: {
        id: 'shareModal.placeholder.emails_mobile',
        defaultMessage: 'Enter recipient emails',
    },
    name: {
        id: 'shareModal.placeholder.name',
        defaultMessage: 'Enter your name',
    },
    message: {
        id: 'shareModal.placeholder.message',
        defaultMessage: 'Add a personalized message (optional)',
    },
    senderEmail: {
        id: 'shareModal.placeholder.sender_email',
        defaultMessage: 'Enter your email',
    },
});

const errorMessages = defineMessages({
    fieldRequired: {
        id: `shareModal.error_message.field_required`,
        defaultMessage: 'This field is required',
    },
    invalidEmail: {
        id: 'shareModal.error_message.invalid_email',
        defaultMessage: 'Invalid Email',
    },
    invalidEmails: {
        id: 'shareModal.error_message.invalid_emails',
        defaultMessage: 'Invalid Email(s)',
    },
});

const defaultCatalogTexts = defineMessages({
    message: {
        id: 'shareModal.defaultTexts.catalog.message',
        defaultMessage:
            'I want to share something beautiful with you! You can discover more at 1stDibs.com.',
    },
});

export const inputLabels = defineMessages({
    message: {
        id: 'shareModal.label.message',
        defaultMessage: 'Include a Message',
    },
});

const textAreaRows = {
    [types.CATALOG]: 4,
};

export class ShareModal extends Component {
    static defaultProps = {
        senderEmail: '',
        senderName: '',
        portfolioIds: [],
        recipientEmails: [],
        selectedItems: [],
        selectedItemImages: [],
        selectedItemTitles: [],

        onClickCopyLink: () => {},
        onShare: () => {},
    };

    constructor(props, context) {
        super(props, context);
        const intl = this.context;

        this.state = {
            validEmails: false,
            validYourEmail: false,
            isSending: false,
            hasError: false,
            emailForm: {
                message:
                    this.props.personalizedMessage ||
                    this.getDefaultText({ intl, fieldName: 'message' }),
                senderName: this.props.senderName,
                senderNameError: '',
                senderEmail: this.props.senderEmail,
                senderEmailError: '',
                recipientEmails: this.props.recipientEmails,
                recipientEmailsError: '',
                showNotes: false,
            },
        };

        this.setStateWrapper = this.setStateWrapper.bind(this);
        this.updateFormValue = this.updateFormValue.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onCancelSharing = this.onCancelSharing.bind(this);
        this.getDefaultText = this.getDefaultText.bind(this);
        this.getValidEmailFields = this.getValidEmailFields.bind(this);
        this.getFormValues = this.getFormValues.bind(this);
        this.getFieldUpdateErrors = this.getFieldUpdateErrors.bind(this);
        this.getSubmitErrors = this.getSubmitErrors.bind(this);
    }

    UNSAFE_componentWillReceiveProps(newProps) {
        const { senderEmail } = newProps;
        const { onAuthBeforeSharing } = this.props;
        const isNonEmptyUpdate = !onAuthBeforeSharing || !!senderEmail.length;
        if (senderEmail !== this.state.emailForm.senderEmail && isNonEmptyUpdate) {
            this.setState({
                emailForm: {
                    ...this.state.emailForm,
                    senderEmail,
                },
            });
        }
    }

    setStateWrapper(name, value) {
        return () => this.setState({ [name]: value });
    }

    getValidEmailFields(formFieldName, value) {
        const { isSubmitInitiallyEnabled } = this.props;

        if (isSubmitInitiallyEnabled) {
            if (formFieldName === 'senderEmail') {
                const validYourEmail = validations.email(value);

                return { validYourEmail };
            } else if (formFieldName === 'recipientEmails') {
                const validEmails = validations.emails(value);

                return { validEmails };
            }
        }

        return {};
    }

    getFieldUpdateErrors(formFieldName, value) {
        const { isSubmitInitiallyEnabled } = this.props;

        if (isSubmitInitiallyEnabled) {
            if (formFieldName === 'message') {
                return {};
            }

            const { hasError } = this.state;
            const intl = this.context;
            const errorField = `${formFieldName}Error`;
            const fieldStateValue = this.state.emailForm[`${errorField}`];
            const fieldError = { [errorField]: '' };

            // empty field
            if (hasError && !value) {
                fieldError[errorField] = intl.formatMessage(errorMessages.fieldRequired);

                return fieldError;
                // invalid sender email
            } else if (formFieldName === 'senderEmail' && value && !validations.email(value)) {
                fieldError[errorField] = intl.formatMessage(errorMessages.invalidEmail);

                return fieldError;
                // invalid recipient emails
            } else if (formFieldName === 'recipientEmails' && value && !validations.emails(value)) {
                fieldError[errorField] = intl.formatMessage(errorMessages.invalidEmails);

                return fieldError;
                // remove existing error
            } else if (fieldStateValue && value) {
                return fieldError;
            }
        }

        return {};
    }

    updateFormValue(formFieldName) {
        return e => {
            let updatedValue;

            switch (formFieldName) {
                case 'recipientEmails':
                    updatedValue = e.target.value.split(',');
                    break;
                case 'showNotes':
                    updatedValue = !this.state.showNotes;
                    break;
                default:
                    // 'senderName', 'senderEmail', 'message'
                    updatedValue = e.target.value;
                    break;
            }

            const fieldError = this.getFieldUpdateErrors(formFieldName, e.target.value);
            const emailForm = {
                ...this.state.emailForm,
                ...fieldError,
                [formFieldName]: updatedValue,
            };
            const validEmails = this.getValidEmailFields(formFieldName, e.target.value);

            this.setState({ ...validEmails, emailForm });
        };
    }

    onCancelSharing() {
        this.setState({ isSending: false });
    }

    getFormValues() {
        const {
            validYourEmail,
            validEmails,
            emailForm: { senderName, senderEmail, recipientEmails },
        } = this.state;
        const { type } = this.props;

        const recipientEmailsValue = recipientEmails ? recipientEmails.join(',') : '';
        const hasValidSenderName = !(!senderName.trim() && typesRequiringName.includes(type));
        const hasValidSenderEmail = senderEmail.length && validYourEmail;
        const isSubmitDisabled =
            !hasValidSenderName || !recipientEmailsValue.length || !validEmails;

        return { isSubmitDisabled, hasValidSenderEmail, hasValidSenderName, recipientEmailsValue };
    }

    getSubmitErrors() {
        const {
            emailForm: { senderName, senderEmail },
        } = this.state;
        const intl = this.context;
        const { recipientEmailsValue } = this.getFormValues();

        const requiredErrorMessage = intl.formatMessage(errorMessages.fieldRequired);

        return {
            senderNameError: senderName ? '' : requiredErrorMessage,
            senderEmailError: senderEmail ? '' : requiredErrorMessage,
            recipientEmailsError: recipientEmailsValue ? '' : requiredErrorMessage,
        };
    }

    onSubmit() {
        const { portfolioIds, type, onAuthBeforeSharing, isSubmitInitiallyEnabled } = this.props;

        const {
            emailForm: { message, senderEmail, senderName, showNotes, recipientEmails },
        } = this.state;

        let inputs = {
            message,
            recipientEmails,
            senderEmail,
            senderName,
        };

        if (type === types.FOLDERS) {
            inputs = {
                ...inputs,
                portfolioIds,
                shareType: types.FOLDERS,
            };
        } else if (type === types.INVOICE) {
            inputs = {
                ...inputs,
                shareType: types.INVOICE,
                transactionId: this.props.transactionId,
            };
        } else if (type === types.PDP) {
            inputs = {
                ...inputs,
                itemId: this.props.selectedItems[0].itemId,
                shareType: types.PDP,
                skuId: this.props.skuId,
            };
        } else if (type === types.DESIGN_PROFILE) {
            inputs = {
                ...inputs,
                shareType: types.DESIGN_PROFILE,
            };
        } else if (type === types.CATALOG) {
            inputs = {
                ...inputs,
                catalogIds: this.props.catalogIds,
                shareType: types.CATALOG,
            };
        } else {
            inputs = {
                ...inputs,
                portfolioItemIds: this.props.selectedItems.map(item => item.portfolioItemId),
                shareType: types.ITEMS,
                showNotes,
            };
        }
        this.setState({ isSending: true });

        if (onAuthBeforeSharing) {
            const onShareItem = () => {
                this.props.onShare({ ...inputs });
            };

            if (!isSubmitInitiallyEnabled) {
                onAuthBeforeSharing(onShareItem, senderName, senderEmail, this.onCancelSharing);
            } else {
                const { isSubmitDisabled } = this.getFormValues();

                if (!isSubmitDisabled) {
                    this.setStateWrapper('hasError', false);
                    onAuthBeforeSharing(onShareItem, senderName, senderEmail, this.onCancelSharing);
                } else {
                    const submitErrors = this.getSubmitErrors();

                    this.setState(prevState => ({
                        isSending: false,
                        hasError: true,
                        emailForm: {
                            ...prevState.emailForm,
                            ...submitErrors,
                        },
                    }));
                }
            }
        } else {
            this.props.onShare({ ...inputs });
        }
    }

    getDefaultText({ intl, fieldName }) {
        const { type } = this.props;

        switch (type) {
            case types.CATALOG: {
                return intl.formatMessage(defaultCatalogTexts[fieldName]) || '';
            }
            default: {
                return '';
            }
        }
    }

    render() {
        const intl = this.context;
        const { type, renderUserAgreement, renderMarketingAgreement, isSubmitInitiallyEnabled } =
            this.props;
        const {
            emailForm: {
                message,
                senderEmail,
                senderName,
                showNotes,
                senderNameError,
                senderEmailError,
                recipientEmailsError,
            },
            isSending,
            hasError,
        } = this.state;
        const portfolioName = this.props.portfolioName || '';
        const portfolioId =
            this.props.portfolioIds && this.props.portfolioIds.length
                ? this.props.portfolioIds[0]
                : '';
        const folderUrl = getFolderLink(portfolioName, portfolioId);
        const invoiceUrl = getInvoiceLink(this.props.transactionId, this.props.invoiceAccessToken);
        const headerTitle = this.props.title || <HeaderTitle type={type} />;
        const submitLabel = <SubmitLabel type={type} />;
        const recipientEmailsPlaceholder = mobileCheck.any() ? 'emailsMobile' : 'emails';
        const { recipientEmailsValue, hasValidSenderName, hasValidSenderEmail } =
            this.getFormValues();
        let { isSubmitDisabled } = this.getFormValues();

        if (type === types.PDP) {
            isSubmitDisabled = isSubmitDisabled || !hasValidSenderEmail;
            if (isSubmitInitiallyEnabled) {
                isSubmitDisabled = false;
            }
        } else if (type === types.DESIGN_PROFILE) {
            isSubmitDisabled = !hasValidSenderName || !hasValidSenderEmail;
        }

        let textAreaLabel;
        if (type === types.CATALOG) {
            textAreaLabel = intl.formatMessage(inputLabels.message);
        }

        return (
            <ModalContainer
                dataTn="share-modal"
                onClose={this.props.closeModal}
                isOpen={this.props.modalOpen}
                modalPosition="center"
            >
                <ModalCloseButton onClick={this.props.closeModal} />
                <BarHeader title={headerTitle} />
                <ModalBody>
                    {this.props.selectedItemImages.length &&
                    type !== types.INVOICE &&
                    type !== types.DESIGN_PROFILE ? (
                        <div className={styles.preview}>
                            <ItemPreviews
                                styleType={type}
                                itemImages={this.props.selectedItemImages}
                                itemTitles={this.props.selectedItemTitles}
                            />
                        </div>
                    ) : null}
                    {type === types.INVOICE && (
                        <div className={styles.invoiceBody}>
                            <FormattedMessage
                                defaultMessage="Share this invoice to let others view your customized purchasing details."
                                id="shareModal.INVOICE.bodyText"
                            />
                            {this.props.isVerifiedTrade && (
                                <span className={styles.verifiedTradeInvoice}>
                                    <FormattedMessage
                                        defaultMessage="Trade members will be able to transfer any custom offers to their account to complete the purchase on your behalf."
                                        id="shareModal.INVOICE.bodyText.trade"
                                    />
                                </span>
                            )}
                        </div>
                    )}
                    {type === types.ITEMS && (
                        <Checkbox
                            name="share-portfolio-modal-notes-checkbox"
                            label={checkboxLabel}
                            labelClass={styles.shareNotesLabel}
                            wrapperClass={styles.shareNotes}
                            checked={showNotes}
                            dataTn="share-modal-notes-checkbox"
                            onChange={this.updateFormValue('showNotes')}
                            disabled={isSending}
                        />
                    )}
                    {type === types.INVOICE ? null : (
                        <div className={styles.input}>
                            <Input
                                dataTn="share-modal-name"
                                hasAnimatedPlaceholder
                                maxLength={100}
                                onChange={this.updateFormValue('senderName')}
                                placeholder={intl.formatMessage(placeholders.name)}
                                value={senderName}
                                disabled={isSending}
                                errorMessage={senderNameError}
                            />
                        </div>
                    )}
                    {(type === types.CATALOG ||
                        type === types.PDP ||
                        type === types.DESIGN_PROFILE) && (
                        <div className={styles.input} data-tn="share-modal-sender-email">
                            <ValidatedInput
                                dataTn="share-modal-email"
                                hasAnimatedPlaceholder
                                showSuccessIcon
                                onChange={this.updateFormValue('senderEmail')}
                                onError={this.setStateWrapper('validYourEmail', false)}
                                onSuccess={this.setStateWrapper('validYourEmail', true)}
                                placeholder={intl.formatMessage(placeholders.senderEmail)}
                                validationType="email"
                                value={senderEmail}
                                validateOnMount={!!senderEmail}
                                disabled={isSending}
                                type="email"
                                canBeUpdatedByForm={isSubmitInitiallyEnabled}
                                formUpdateErrorMessage={senderEmailError}
                                isRequiredField={isSubmitInitiallyEnabled && hasError}
                                requiredFieldErrorMessage={intl.formatMessage(
                                    errorMessages.fieldRequired
                                )}
                            />
                        </div>
                    )}
                    {type !== types.DESIGN_PROFILE && (
                        <div className={styles.input} data-tn="share-modal-recipient-email">
                            <ValidatedInput
                                dataTn="share-modal-email"
                                hasAnimatedPlaceholder
                                showSuccessIcon
                                onChange={this.updateFormValue('recipientEmails')}
                                onError={this.setStateWrapper('validEmails', false)}
                                onSuccess={this.setStateWrapper('validEmails', true)}
                                placeholder={intl.formatMessage(
                                    placeholders[recipientEmailsPlaceholder]
                                )}
                                validationType="emails"
                                value={recipientEmailsValue}
                                disabled={isSending}
                                type="email"
                                canBeUpdatedByForm={isSubmitInitiallyEnabled}
                                formUpdateErrorMessage={recipientEmailsError}
                                isRequiredField={isSubmitInitiallyEnabled && hasError}
                                requiredFieldErrorMessage={intl.formatMessage(
                                    errorMessages.fieldRequired
                                )}
                            />
                        </div>
                    )}
                    <div className={styles.textArea}>
                        <TextArea
                            dataTn="share-modal-message"
                            rows={textAreaRows[type]}
                            label={textAreaLabel}
                            maxLength={500}
                            onChange={this.updateFormValue('message')}
                            placeholder={intl.formatMessage(placeholders.message)}
                            isMobile
                            value={message}
                            disabled={isSending}
                        />
                    </div>
                    {renderMarketingAgreement && renderMarketingAgreement()}
                    <Submit
                        label={submitLabel}
                        isSending={isSending}
                        disabled={isSubmitDisabled}
                        onClick={this.onSubmit}
                    />
                    {type === types.INVOICE ? null : (
                        <div className={styles.shareInfo}>
                            <FormattedMessage
                                id="shareModal.footer"
                                defaultMessage="You will receive a copy of this email."
                            />
                        </div>
                    )}
                    {renderUserAgreement && renderUserAgreement()}
                </ModalBody>
                {type === types.FOLDERS && (
                    <CopyToClipboard
                        url={folderUrl}
                        type={type}
                        onClickCopyLink={this.props.onClickCopyLink}
                    />
                )}
                {type === types.INVOICE && (
                    <CopyToClipboard
                        url={invoiceUrl}
                        type={type}
                        onClickCopyLink={this.props.onClickCopyLink}
                    />
                )}
            </ModalContainer>
        );
    }
}

ShareModal.contextType = IntlContext;

ShareModal.propTypes = {
    catalogIds: PropTypes.shape({
        rootId: PropTypes.string,
        pageTypeId: PropTypes.string,
    }),
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    personalizedMessage: PropTypes.string,
    portfolioName: PropTypes.string,
    senderName: PropTypes.string,
    senderEmail: PropTypes.string,
    transactionId: PropTypes.string,
    invoiceAccessToken: PropTypes.string,
    isVerifiedTrade: PropTypes.bool,
    onClickCopyLink: PropTypes.func,
    skuId: PropTypes.string,

    type: PropTypes.oneOf([
        types.CATALOG,
        types.FOLDERS,
        types.ITEMS,
        types.INVOICE,
        types.PDP,
        types.DESIGN_PROFILE,
    ]),
    portfolioIds: PropTypes.array,
    recipientEmails: PropTypes.array,
    selectedItems: PropTypes.array,
    selectedItemImages: PropTypes.array,
    selectedItemTitles: PropTypes.array,

    modalOpen: PropTypes.bool,

    closeModal: PropTypes.func,
    onShare: PropTypes.func,
    onAuthBeforeSharing: PropTypes.func,
    renderUserAgreement: PropTypes.func,
    renderMarketingAgreement: PropTypes.func,
    isSubmitInitiallyEnabled: PropTypes.bool,
};

export default ShareModal;
